import { escapeHtml, formatMenuPrice } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn, type MenuPage } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import {
  ARABIC_LINE,
  badge,
  bilingual,
  description,
  hasTitle,
  image,
  lineCount,
  pagePhotos,
  priceBlock,
  sectionSubtitle,
  sectionTitle,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Food collage: a slate chalkboard with photos of the dishes tiled among the lists, a script
 * line over huge capitals for the title, and solid labels for the section headings. This file
 * holds the mosaic layout and the pieces the other layouts of the set share.
 */

/* ------------------------------------------------------------------ shared look */

export const FC_THEME: MenuTheme = {
  backgroundColor: '#25282b',
  surfaceColor: '#33373b',
  textColor: '#ffffff',
  primaryColor: '#ffffff',
  accentColor: '#ffe01b',
  fontId: 'modern',
  backgroundMediaId: null,
};

export const FC_VARIANTS: MenuStyleVariant[] = [
  { id: 'slate', name: 'Slate & yellow', theme: {} },
  { id: 'black', name: 'Black & orange', theme: { backgroundColor: '#121212', surfaceColor: '#1f1f1f', accentColor: '#ff8a1f' } },
  { id: 'navy', name: 'Navy & lime', theme: { backgroundColor: '#15213a', surfaceColor: '#1e2d4c', accentColor: '#c9f23e' } },
  { id: 'brown', name: 'Brown & cream', theme: { backgroundColor: '#3a2a20', surfaceColor: '#4a362a', textColor: '#fbf4e6', accentColor: '#f3dfb4', fontId: 'cafe' } },
];

// Fine noise over a soft vignette reads as chalk dust on slate.
const CHALK =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 .08 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")";

/** Which of an English/Arabic pair `bilingual` draws on this board. */
function shown(board: MenuBoardContent, en: string | null | undefined, ar: string | null | undefined) {
  const showEn = board.settings.language !== 'ar';
  const showAr = board.settings.language !== 'en';
  const hasEn = Boolean(en?.trim());
  const hasAr = Boolean(ar?.trim());
  const drawAr = showAr && hasAr;
  const drawEn = hasEn && (showEn || !drawAr);
  return { en: drawEn, ar: drawAr, both: drawEn && drawAr };
}

/** Distinct item photos on the board, in board order. */
function boardPhotoIds(board: MenuBoardContent): string[] {
  return [...new Set(board.items.map((i) => i.imageMediaId).filter((id): id is string => Boolean(id)))];
}

/** How many photo tiles a layout shows: as many as it has room for, if the board has them. */
export function fcPhotoCount(board: MenuBoardContent, max: number): number {
  return Math.min(max, boardPhotoIds(board).length);
}

/**
 * Photos for the tiles: dishes on this page first, then the board's other dishes, so the tiles
 * stay filled on a page whose items have few photos.
 */
export function fcPhotoIds(ctx: RenderContext, count: number): string[] {
  const onPage = pagePhotos(ctx, 99).map((i) => i.imageMediaId!);
  return [...new Set([...onPage, ...boardPhotoIds(ctx.board)])].slice(0, count);
}

export function fcPhoto(ctx: RenderContext, id: string | undefined, p: string, extra = ''): string {
  return `<div class="${p}-ph${extra ? ` ${extra}` : ''}">${id ? image(ctx, id, `${p}-img`) : ''}</div>`;
}

/* ---------------------------------------------------------------------- title */

export interface FcTitleSpec {
  /** Room across, in em. */
  width: number;
  /** Script line and capitals at full size, in em. */
  script: number;
  caps: number;
  maxLines: number;
}

export interface FcTitleLayout {
  caps: number;
  capsAr: number;
  height: number;
}

/** Bold capitals run about 0.8em a letter; Arabic about half that. */
const CAP_WIDTH = 0.8;
const AR_WIDTH = 0.5;

function wrapLines(words: number[], perLine: number): number {
  let lines = 1;
  let used = 0;
  for (const w of words) {
    if (used > 0 && used + 1 + w > perLine) {
      lines += 1;
      used = w;
    } else used += (used > 0 ? 1 : 0) + w;
  }
  return lines;
}

/** Sizes the title to its box and estimates how tall the block is, in root em. */
export function fcTitleLayout(board: MenuBoardContent, spec: FcTitleSpec): FcTitleLayout {
  const title = board.settings.title ?? board.name;
  const titleAr = board.settings.titleAr;
  const t = shown(board, title, titleAr);
  const b = shown(board, board.brand.name, board.brand.nameAr);
  const g = shown(board, board.brand.tagline, board.brand.taglineAr);

  let caps = spec.caps;
  let lines = 0;
  if (t.en) {
    const words = title!.trim().split(/\s+/).map((w) => w.length);
    const longest = Math.max(...words);
    caps = Math.min(spec.caps, spec.width / (longest * CAP_WIDTH));
    for (let i = 0; i < 12; i += 1) {
      lines = wrapLines(words, spec.width / (caps * CAP_WIDTH));
      if (lines <= spec.maxLines) break;
      caps *= 0.9;
    }
  }
  const arLength = titleAr?.trim().length ?? 0;
  const arBase = t.en ? spec.caps * 0.5 : spec.caps * 0.8;
  const capsAr = arLength ? Math.min(arBase, spec.width / (arLength * AR_WIDTH)) : arBase;

  let height = 0.6;
  if (b.en) height += spec.script * 1.15;
  if (b.ar) height += (b.en ? spec.script * 0.7 : spec.script) * ARABIC_LINE;
  if (t.en) height += lines * caps + 0.15;
  if (t.ar) height += capsAr * 1.35 + 0.1;
  if (g.en || g.ar) height += 0.5 + (g.en ? 1.25 : 0) + (g.ar ? 1.35 : 0);
  return { caps, capsAr, height };
}

export function fcTitle(ctx: RenderContext, p: string, layout: FcTitleLayout): string {
  const { board } = ctx;
  return [
    `<div class="${p}-title" style="--caps:${layout.caps.toFixed(2)}em;--capsar:${layout.capsAr.toFixed(2)}em">`,
    bilingual(ctx, board.brand.name, board.brand.nameAr, `${p}-script`),
    bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, `${p}-caps`),
    bilingual(ctx, board.brand.tagline, board.brand.taglineAr, `${p}-tag`),
    '</div>',
  ].join('');
}

/* ----------------------------------------------------------------------- lists */

/**
 * Whether an item's sizes fit on its name line in a column `width` em across; otherwise they
 * get a line of their own. Estimated from character counts at the row's type sizes.
 */
function sizesInline(board: MenuBoardContent, item: MenuItemContent, width: number): boolean {
  if (!item.variants.length) return true;
  const arOnly = board.settings.language === 'ar';
  const name = arOnly && item.titleAr?.trim() ? item.titleAr.trim().length * 0.6 : item.title.trim().length * 0.82;
  const sizes = item.variants.reduce((sum, v) => {
    const label = arOnly ? v.labelAr?.trim() || v.label : v.label;
    return sum + label.length * 0.55 + 0.3 + formatMenuPrice(v.price, board.settings.currency).length * 0.78 + 1.1;
  }, 0);
  const badges = (item.badge || item.badgeAr ? 3.5 : 0) + (item.discountPrice != null ? 3 : 0);
  return name + 0.6 + sizes + badges <= width * 0.95;
}

/** Sizes go on the name line only if they fit there for every sized item of the block, so a list reads evenly. */
function blockInline(board: MenuBoardContent, items: MenuItemContent[], width: number): boolean {
  return items.every((i) => sizesInline(board, i, width));
}

function row(ctx: RenderContext, item: MenuItemContent, p: string, inline: boolean): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="${p}-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="${p}-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="${p}-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  const badges = badge(ctx, item, `${p}-badge`);
  const sized = item.variants.length > 0 && !inline;
  return [
    `<div class="${p}-item">`,
    `<div class="${p}-line">${name}${badges ? `<span class="${p}-badges">${badges}</span>` : ''}${sized ? '' : priceBlock(ctx, item, p)}</div>`,
    second,
    sized ? `<div class="${p}-sizerow">${priceBlock(ctx, item, p)}</div>` : '',
    description(ctx, item, `${p}-desc`),
    '</div>',
  ].join('');
}

/** A section under its solid label. */
export function fcBlock(ctx: RenderContext, b: MenuBlock, p: string, width: number): string {
  const head = hasTitle(b.section) ? `<div class="${p}-label">${sectionTitle(ctx, b.section, `${p}-lt`)}</div>` : '';
  const sub = b.continued ? '' : sectionSubtitle(ctx, b.section, `${p}-sub`);
  return `<div class="${p}-block">${head}${sub}${b.items.map((i) => row(ctx, i, p, blockInline(ctx.board, b.items, width))).join('')}</div>`;
}

export function fcBlocks(ctx: RenderContext, blocks: MenuBlock[] | undefined, p: string, width: number): string {
  return (blocks ?? []).map((b) => fcBlock(ctx, b, p, width)).join('');
}

/** Item heights in em, matching fcCss: a name line, Arabic name, sizes, description, gap. */
function itemCost(board: MenuBoardContent, item: MenuItemContent, width: number, inline: boolean): number {
  const perLine = Math.floor(width * 1.95);
  const arOnly = board.settings.language === 'ar';
  const both = board.settings.language === 'both';
  let h = 1.3 * (arOnly && item.titleAr ? ARABIC_LINE : 1.25);
  if (both && item.titleAr?.trim()) h += 1.05 * ARABIC_LINE + 0.05;
  if (item.variants.length && !inline) h += 1.65;
  if (board.settings.showDescriptions) {
    const d = shown(board, item.description, item.descriptionAr);
    const lines = (d.en ? lineCount(item.description, perLine, 2) : 0) + (d.ar ? lineCount(item.descriptionAr, perLine * 0.9, 2) : 0);
    h += lines ? 0.15 + lines * 0.85 * 1.32 : 0;
  }
  return h + 0.75;
}

function headCost(board: MenuBoardContent, section: MenuSection, continued: boolean): number {
  let h = 0;
  if (hasTitle(section)) {
    const t = shown(board, section.title, section.titleAr);
    h += 0.9 + (t.en ? 1.6 * 1.15 : 0) + (t.ar ? (t.en ? 1.25 : 1.6) * ARABIC_LINE : 0) + 1;
  }
  if (!continued && (section.subtitle?.trim() || section.subtitleAr?.trim())) {
    const s = shown(board, section.subtitle, section.subtitleAr);
    h += (s.en ? 1.4 * 1.25 : 0) + (s.ar ? 1.2 * ARABIC_LINE : 0) + 0.2;
  }
  return h;
}

/** A list column `width` em across holding `capacity` em of blocks. */
export function fcColumn(board: MenuBoardContent, capacity: number, width: number): MenuColumn {
  return {
    capacity: capacity * 0.97,
    blockCost: (section, items, continued) =>
      headCost(board, section, continued) + items.reduce((sum, item) => sum + itemCost(board, item, width, blockInline(board, items, width)), 0) + 1.6,
  };
}

export function fcPaginate(board: MenuBoardContent, columns: MenuColumn[]): MenuPage[] {
  return packColumns(groupBySection(board), columns, { balance: true });
}

export function fcEmpty(ctx: RenderContext): string {
  return ctx.page.columns.some((c) => c.length > 0) ? '' : '<div class="mb-empty">No dishes on this board yet</div>';
}

/** Background, title, labels, rows and photo tiles for one design of the set. */
export function fcCss(S: string, p: string): string {
  return `
${S}{padding:2em 2.4em;
  background-image:${CHALK},radial-gradient(ellipse at 50% 35%,rgba(255,255,255,.09),transparent 70%),
    linear-gradient(to bottom,transparent 55%,rgba(0,0,0,.28))}
${S} .${p}-title{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;min-width:0;--script:3em}
${S} .${p}-script{font-family:var(--mb-font-script);color:var(--mb-primary);font-size:var(--script);line-height:1.15;
  text-shadow:0 .06em .3em rgba(0,0,0,.4)}
${S} .${p}-script.mb-2nd{font-size:calc(var(--script) * .7)}
${S} .${p}-caps{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-accent);font-size:var(--caps);
  line-height:1;text-transform:uppercase;letter-spacing:.01em;text-shadow:0 .05em .25em rgba(0,0,0,.35);overflow-wrap:anywhere}
${S} .${p}-caps.mb-ar{font-size:var(--capsar);line-height:1.35;text-transform:none}
${S} .${p}-caps.mb-2nd{margin-top:.1em}
${S} .${p}-tag{font-size:.95em;font-weight:600;letter-spacing:.2em;text-transform:uppercase;opacity:.85;margin-top:.5em;line-height:1.25}
${S} .${p}-tag.mb-ar{letter-spacing:0}
${S} .${p}-tag.mb-2nd{margin-top:0}
${S} .${p}-list{min-height:0;min-width:0;overflow:hidden;display:flex;flex-direction:column}
${S} .${p}-block{display:flex;flex-direction:column;margin-bottom:1.6em}
${S} .${p}-label{align-self:flex-start;min-width:62%;max-width:100%;background:var(--mb-accent);color:var(--mb-bg);
  padding:.45em 1.1em;margin-bottom:1em;border-radius:.15em;text-align:center;box-shadow:0 .35em .9em rgba(0,0,0,.3)}
${S} .${p}-lt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.6em;line-height:1.15;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-lt.mb-2nd{font-size:1.25em}
${S} .${p}-sub{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:1.4em;line-height:1.25;margin:-.45em 0 .55em}
${S} .${p}-sub.mb-2nd{font-size:1.2em;margin-top:-.3em}
${S} .${p}-item{margin-bottom:.75em}
${S} .${p}-line{display:flex;align-items:baseline;gap:.6em}
${S} .${p}-name{flex:1 1 auto;min-width:0;font-weight:800;font-size:1.3em;line-height:1.25;color:var(--mb-text);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-name.mb-ar{line-height:1.3}
${S} .${p}-name2{font-weight:700;font-size:1.05em;color:var(--mb-text);opacity:.88;margin-top:.05em;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-price{font-weight:800;font-size:1.3em;color:var(--mb-accent);white-space:nowrap}
${S} .${p}-pricebox{display:flex;align-items:baseline;gap:.45em;flex-shrink:0}
${S} .${p}-old{font-size:1em;font-weight:600;opacity:.55;white-space:nowrap}
${S} .${p}-sizerow{display:flex;justify-content:flex-end;margin-top:.15em}
${S} .${p}-sizes{display:flex;gap:1.1em;white-space:nowrap}
${S} .${p}-size small{font-size:.85em;font-weight:600;opacity:.8;margin-inline-end:.25em}
${S} .${p}-size .${p}-price{font-size:1.15em}
${S} .${p}-badges{display:flex;gap:.3em;flex-shrink:0}
${S} .${p}-badge{font-size:.75em;font-weight:800;padding:.15em .55em;border-radius:99em;background:var(--mb-accent);color:var(--mb-bg);white-space:nowrap}
${S} .${p}-desc{font-size:.85em;line-height:1.3;opacity:.7;margin-top:.15em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .${p}-ph{position:relative;min-height:0;border-radius:.7em;overflow:hidden;background:var(--mb-surface);
  box-shadow:0 .8em 1.8em rgba(0,0,0,.45),0 0 0 .12em rgba(255,255,255,.06)}
${S} .${p}-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
`;
}

/* ---------------------------------------------------------------------- mosaic */

const S = '.mb-t-food-collage-mosaic';
const P = 'fcm';

/** Inner height of the screen in em, after the padding. */
const H = { landscape: 63.5, portrait: 116 };
const PHOTO = { landscape: 15, portrait: 15.5 };
const GAP = 1.4;
/** List column widths in em. */
const WIDTH = { landscape: 27, portrait: 30 };
const TITLE_LANDSCAPE: FcTitleSpec = { width: 26, script: 3, caps: 4.9, maxLines: 3 };
const TITLE_PORTRAIT: FcTitleSpec = { width: 60, script: 3.4, caps: 6.2, maxLines: 2 };

/**
 * Photos per column. Landscape: two over the first list, one under the title, two under the
 * third list; the first ones placed are the ones that matter most to the look.
 */
function photoSlots(n: number, portrait: boolean): number[] {
  if (portrait) return [(n >= 1 ? 1 : 0) + (n >= 3 ? 1 : 0), (n >= 2 ? 1 : 0) + (n >= 4 ? 1 : 0)];
  return [(n >= 1 ? 1 : 0) + (n >= 4 ? 1 : 0), n >= 3 ? 1 : 0, (n >= 2 ? 1 : 0) + (n >= 5 ? 1 : 0), 0];
}

function geometry(board: MenuBoardContent, portrait: boolean) {
  const slots = photoSlots(fcPhotoCount(board, portrait ? 4 : 5), portrait);
  const title = fcTitleLayout(board, portrait ? TITLE_PORTRAIT : TITLE_LANDSCAPE);
  const photo = portrait ? PHOTO.portrait : PHOTO.landscape;
  if (portrait) {
    const free = H.portrait - title.height - 1.8;
    return {
      slots,
      title,
      columns: slots.map((k) => fcColumn(board, free - k * (photo + GAP), WIDTH.portrait)),
    };
  }
  return {
    slots,
    title,
    columns: slots.map((k, i) => fcColumn(board, H.landscape - k * (photo + GAP) - (i === 1 ? title.height + GAP : 0), WIDTH.landscape)),
  };
}

export const foodCollageMosaic: MenuTemplate = {
  id: 'food-collage-mosaic',
  name: 'Food collage · Mosaic',
  description:
    'A chalkboard collage: dish photos tiled among lists with bold yellow labels, and a big script-and-capitals title in the mix.',
  defaultTheme: FC_THEME,
  variants: FC_VARIANTS,
  cuisines: ['burgers', 'pizza', 'pasta'],
  family: { id: 'food-collage', name: 'Food Collage', layout: 'Mosaic' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return fcPaginate(board, geometry(board, orientation === 'portrait').columns);
  },

  render(ctx) {
    const portrait = ctx.orientation === 'portrait';
    const { slots, title } = geometry(ctx.board, portrait);
    const ids = fcPhotoIds(ctx, slots.reduce((a, b) => a + b, 0));
    // Tiles are handed out in the order photoSlots fills them, so the best photo lands first.
    const order = portrait ? [0, 1, 0, 1] : [0, 2, 1, 0, 2];
    const perColumn: string[][] = slots.map(() => []);
    ids.forEach((id, i) => perColumn[order[i]!]!.push(fcPhoto(ctx, id, P)));
    const list = (i: number) => `<div class="${P}-list">${fcBlocks(ctx, ctx.page.columns[i], P, portrait ? WIDTH.portrait : WIDTH.landscape)}${i === 0 ? fcEmpty(ctx) : ''}</div>`;
    const titleHtml = fcTitle(ctx, P, title);

    if (portrait) {
      return [
        titleHtml,
        '<div class="fcm-grid">',
        `<div class="fcm-col">${perColumn[0]!.join('')}${list(0)}</div>`,
        `<div class="fcm-col">${list(1)}${perColumn[1]!.join('')}</div>`,
        '</div>',
      ].join('');
    }
    return [
      '<div class="fcm-grid">',
      `<div class="fcm-col">${perColumn[0]!.join('')}${list(0)}</div>`,
      `<div class="fcm-col">${titleHtml}${list(1)}${perColumn[1]!.join('')}</div>`,
      `<div class="fcm-col">${list(2)}${perColumn[2]!.join('')}</div>`,
      `<div class="fcm-col">${list(3)}</div>`,
      '</div>',
    ].join('');
  },

  css:
    fcCss(S, P) +
    `
${S} .fcm-grid{flex:1;min-height:0;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:0 2.2em}
${S}.mb-portrait .fcm-grid{grid-template-columns:repeat(2,minmax(0,1fr));margin-top:1.8em}
${S} .fcm-col{min-height:0;min-width:0;display:flex;flex-direction:column;gap:${GAP}em}
${S} .fcm-list{flex:1}
${S} .fcm-ph{flex:0 0 ${PHOTO.landscape}em}
${S}.mb-portrait .fcm-ph{flex-basis:${PHOTO.portrait}em}
${S} .fcm-col>.fcm-list:first-child{padding-top:.2em}
${S} .fcm-col>.fcm-ph+.fcm-list{padding-top:.4em}
${S}.mb-portrait .fcm-title{--script:3.4em}
`,
};
